/**
 * Security utilities for input validation, sanitization, and protection
 */

import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(dirty: string): string {
  if (typeof window === 'undefined') {
    // Server-side: basic HTML escaping
    return dirty
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  
  // Client-side: use DOMPurify
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit']
  });
}

/**
 * Validates and sanitizes user input for forms
 * @param input - The user input string
 * @param options - Validation options
 * @returns Sanitized and validated input
 */
export function validateInput(input: string, options: {
  maxLength?: number;
  allowHtml?: boolean;
  pattern?: RegExp;
  required?: boolean;
} = {}): { isValid: boolean; sanitized: string; error?: string } {
  const { maxLength = 1000, allowHtml = false, pattern, required = false } = options;
  
  // Check if required field is empty
  if (required && (!input || input.trim().length === 0)) {
    return {
      isValid: false,
      sanitized: '',
      error: 'This field is required'
    };
  }
  
  // Check length
  if (input.length > maxLength) {
    return {
      isValid: false,
      sanitized: input.substring(0, maxLength),
      error: `Input exceeds maximum length of ${maxLength} characters`
    };
  }
  
  // Sanitize based on HTML allowance
  const sanitized = allowHtml ? sanitizeHtml(input) : escapeHtml(input);
  
  // Validate against pattern if provided
  if (pattern && !pattern.test(sanitized)) {
    return {
      isValid: false,
      sanitized,
      error: 'Input format is invalid'
    };
  }
  
  return {
    isValid: true,
    sanitized
  };
}

/**
 * Escapes HTML characters to prevent XSS
 * @param text - Text to escape
 * @returns Escaped text
 */
export function escapeHtml(text: string): string {
  const div = typeof window !== 'undefined' ? document.createElement('div') : null;
  if (div) {
    div.textContent = text;
    return div.innerHTML;
  }
  
  // Fallback for server-side
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Validates email format
 * @param email - Email to validate
 * @returns Whether email is valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254; // RFC 5321 limit
}

/**
 * Validates phone number format
 * @param phone - Phone number to validate
 * @returns Whether phone number is valid
 */
export function isValidPhone(phone: string): boolean {
  // Remove all non-digit characters for validation
  const digits = phone.replace(/\D/g, '');
  // Check for valid US phone number (10-11 digits)
  return digits.length >= 10 && digits.length <= 11;
}

/**
 * Validates URL format and ensures it's safe
 * @param url - URL to validate
 * @returns Whether URL is valid and safe
 */
export function isValidUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
}

/**
 * Rate limiting helper using localStorage
 * @param key - Unique key for the rate limit
 * @param limit - Number of attempts allowed
 * @param windowMs - Time window in milliseconds
 * @returns Whether the action is allowed
 */
export function isRateLimited(key: string, limit: number = 5, windowMs: number = 60000): boolean {
  if (typeof window === 'undefined') return false;
  
  const now = Date.now();
  const storageKey = `rate_limit_${key}`;
  
  try {
    const stored = localStorage.getItem(storageKey);
    const attempts = stored ? JSON.parse(stored) : { count: 0, resetTime: now + windowMs };
    
    // Reset if window has expired
    if (now > attempts.resetTime) {
      attempts.count = 0;
      attempts.resetTime = now + windowMs;
    }
    
    // Check if limit exceeded
    if (attempts.count >= limit) {
      return true; // Rate limited
    }
    
    // Increment count and store
    attempts.count++;
    localStorage.setItem(storageKey, JSON.stringify(attempts));
    
    return false; // Not rate limited
  } catch (error) {
    console.warn('Rate limiting error:', error);
    return false; // Allow on error
  }
}

/**
 * Generates a secure random string
 * @param length - Length of the string
 * @returns Random string
 */
export function generateSecureToken(length: number = 32): string {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  
  // Fallback for environments without crypto API
  return Math.random().toString(36).substring(2, length + 2);
}

/**
 * Validates file upload security
 * @param file - File to validate
 * @param options - Validation options
 * @returns Validation result
 */
export function validateFileUpload(file: File, options: {
  maxSize?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
} = {}): { isValid: boolean; error?: string } {
  const { 
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.pdf']
  } = options;
  
  // Check file size
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `File size exceeds ${Math.round(maxSize / 1024 / 1024)}MB limit`
    };
  }
  
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'File type not allowed'
    };
  }
  
  // Check file extension
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!allowedExtensions.includes(extension)) {
    return {
      isValid: false,
      error: 'File extension not allowed'
    };
  }
  
  return { isValid: true };
}

/**
 * Logs security events for monitoring
 * @param event - Security event details
 */
export function logSecurityEvent(event: {
  type: 'validation_failed' | 'rate_limit_exceeded' | 'suspicious_activity' | 'xss_attempt';
  details: string;
  userAgent?: string;
  ip?: string;
}): void {
  // In production, this would send to a security monitoring service
  console.warn('Security Event:', {
    ...event,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : 'unknown'
  });
}